import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { reportService } from "../services/api";
import { pdfService } from "../services/pdfService";
import { useLanguage } from "../context/LanguageContext";
import { AuthContext } from "../context/AuthContext";
import { buildSearchQuery } from "../utils/searchUtils";
import moment from "moment";
import SignatureCanvas from "react-signature-canvas";

// Material UI components
import {
  Box, Button, Card, CardContent, CardHeader, CardActions, Chip, CircularProgress, Divider, Grid, IconButton,
  InputAdornment, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TableSortLabel, TextField, Tooltip, Typography, Alert, AlertTitle, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle, ListItemText, List, ListItem, ListItemIcon, Skeleton,
  Snackbar, Switch, FormControlLabel, useTheme
} from "@mui/material";

// Material UI icons
import {
  Add as AddIcon, Search as SearchIcon, Clear as ClearIcon,
  VisibilityOutlined as VisibilityIcon, DescriptionOutlined as DescriptionIcon,
  CheckCircleOutline as CheckCircleIcon, WarningAmberOutlined as WarningIcon,
  CalendarTodayOutlined as CalendarMonthIcon,
  BuildOutlined as BuildIcon,
  GridViewOutlined as GridViewIcon,
  ViewListOutlined as ViewListIcon,
  PictureAsPdfOutlined as PictureAsPdfIcon,
  DoneAll as DoneAllIcon, EditOutlined as EditIcon, AccountCircleOutlined as AccountCircleIcon,
  UnfoldMore as UnfoldMoreIcon, Person as PersonIcon
} from "@mui/icons-material";

import PageContainer from "../components/PageContainer";
import ContentSection from "../components/ContentSection";
import SearchBar from "../components/SearchBar";

const ReportList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser, isManager } = useContext(AuthContext);
  const { t } = useLanguage();
  const theme = useTheme();

  const [allReports, setAllReports] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState(searchParams.get("view") || "table");
  const [showQuickViewModal, setShowQuickViewModal] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);

  // Search state for instant search bar
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  // Remove debouncedSearchTerm, only use searchTerm

  const sigPadRef = useRef(null);
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [bulkSigning, setBulkSigning] = useState(false);
  const [bulkSignError, setBulkSignError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [signatureError, setSignatureError] = useState(null);
  const [useProfileSignature, setUseProfileSignature] = useState(true);

  // Remove all filters, only use search bar

  const [sortBy, setSortBy] = useState(searchParams.get("sortBy") || "createdAt");
  const [sortDirection, setSortDirection] = useState(searchParams.get("sortDir") || "desc");

  const [pagination, setPagination] = useState({
    currentPage: parseInt(searchParams.get("page") || "0"),
    pageSize: parseInt(searchParams.get("limit") || (viewMode === "table" ? 10 : 9)),
    totalItems: 0,
  });

  // ...existing code...

  // Computed values
  const hasUnsignedReports = reports.some(r => r.status?.toLowerCase() === "unsigned" || r.status?.toLowerCase() === "pending");

  // Helper function to check if a report has incomplete checks
  const hasIncompleteChecks = (report) => {
    if (!report.checks) return false;

    const checklistCategories = ["exterior", "interior", "engine", "cleaning"];

    for (const category of checklistCategories) {
      if (Array.isArray(report.checks[category])) {
        // Handle array structure from CreateReport form
        const incompleteChecks = report.checks[category].filter(check => {
          if (!check || !check.name || check.name.trim() === '') return false;
          return !["ok", "passed", "completed"].includes(check.status?.toLowerCase());
        });
        if (incompleteChecks.length > 0) return true;
      } else if (report.checks[category] && typeof report.checks[category] === 'object') {
        // Handle flat object structure
        const incompleteChecks = Object.entries(report.checks[category]).filter(([key, value]) => {
          if (!key || key === '' || key.match(/^\d+$/) || key.length <= 1) return false;
          const isCompleted = typeof value === 'boolean' ? value : (value?.status === 'completed' || value === true);
          return !isCompleted;
        });
        if (incompleteChecks.length > 0) return true;
      }
    }
    return false;
  };

  // Helper function to render check status icon
  const renderCheckStatusIcon = (report) => {
    const hasIssues = hasIncompleteChecks(report);

    if (!report.checks) {
      return (
        <Tooltip title={t("noChecklistData")}>
          <WarningIcon fontSize="small" color="disabled" />
        </Tooltip>
      );
    }

    if (hasIssues) {
      return (
        <Tooltip title={t("hasIncompleteChecks")}>
          <WarningIcon fontSize="small" color="warning" />
        </Tooltip>
      );
    }

    return (
      <Tooltip title={t("allChecksCompleted")}>
        <CheckCircleIcon fontSize="small" color="success" />
      </Tooltip>
    );
  };

  // Fetch all reports once, then filter/sort/paginate client-side
  const fetchAllReports = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const reportsRes = await reportService.getAll({ limit: 10000 });
      setAllReports(Array.isArray(reportsRes.data?.reports) ? reportsRes.data.reports : []);
    } catch (err) {
      console.error("Error fetching all reports:", err);
      setError(t("errorFetchingReports") + (err.message ? `: ${err.message}` : ''));
      setAllReports([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAllReports();
  }, [fetchAllReports]);

  // Filtering, sorting, and pagination logic in useMemo/useEffect
  const processedReports = React.useMemo(() => {
    let filtered = [...allReports];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      // Date regexes: dd.mm.yyyy, dd.mm, dd/mm/yyyy, dd/mm
      const dateRegexes = [
        /\b(\d{2})[.](\d{2})[.](\d{4})\b/, // dd.mm.yyyy
        /\b(\d{2})[.](\d{2})\b/,           // dd.mm
        /\b(\d{2})\/(\d{2})\/(\d{4})\b/, // dd/mm/yyyy
        /\b(\d{2})\/(\d{2})\b/            // dd/mm
      ];
      filtered = allReports.filter((report) => {
        // Search in excavator manufacturer/model
        const excavatorMatch = report.excavator && (
          (report.excavator.manufacturer && report.excavator.manufacturer.toLowerCase().includes(term)) ||
          (report.excavator.model && report.excavator.model.toLowerCase().includes(term))
        );
        // Search in operator name
        const operatorMatch = report.operator && (
          (typeof report.operator === 'string' && report.operator.toLowerCase().includes(term)) ||
          (report.operator.name && report.operator.name.toLowerCase().includes(term))
        );
        // Search in status
        const statusMatch = report.status && report.status.toLowerCase().includes(term);
        // Search in date fields
        let dateMatch = false;
        const dateFields = [report.maintenanceDate, report.createdAt];
        for (const date of dateFields) {
          if (!date) continue;
          const m = moment(date);
          if (!m.isValid()) continue;
          // Check all date formats
          for (const regex of dateRegexes) {
            if (regex.test(term)) {
              // Try to match formatted date
              const formatted = m.format(regex.source.includes('yyyy') ? 'DD.MM.YYYY' : 'DD.MM');
              if (formatted.toLowerCase().includes(term)) { dateMatch = true; break; }
              // Also try DD/MM/YYYY and DD/MM
              const formattedSlash = m.format(regex.source.includes('yyyy') ? 'DD/MM/YYYY' : 'DD/MM');
              if (formattedSlash.toLowerCase().includes(term)) { dateMatch = true; break; }
            }
          }
          // Also allow partial year/month/day search
          if (m.format('DD.MM.YYYY').toLowerCase().includes(term) || m.format('DD/MM/YYYY').toLowerCase().includes(term)) { dateMatch = true; }
        }
        // Fallback: search all string fields
        const genericMatch = Object.values(report).some(val => typeof val === 'string' && val.toLowerCase().includes(term));
        return excavatorMatch || operatorMatch || statusMatch || dateMatch || genericMatch;
      });
    }
    filtered.sort((a, b) => {
      let valA = a[sortBy] || "";
      let valB = b[sortBy] || "";
      valA = String(valA).toLowerCase();
      valB = String(valB).toLowerCase();
      return sortDirection === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });
    return filtered;
  }, [allReports, searchTerm, sortBy, sortDirection]);

  useEffect(() => {
    const paginatedData = processedReports.slice(
      pagination.currentPage * pagination.pageSize,
      pagination.currentPage * pagination.pageSize + pagination.pageSize
    );
    setReports(paginatedData);
    setPagination(prev => ({ ...prev, totalItems: processedReports.length }));
  }, [processedReports, pagination.currentPage, pagination.pageSize]);

  // Sorting handler (must be inside component)
  const handleSort = (field) => {
    const isAsc = sortBy === field && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortBy(field);
    setPagination(prev => ({ ...prev, currentPage: 0 })); // Reset page on sort
  };

  const handleQuickView = (report) => { setCurrentReport(report); setShowQuickViewModal(true); };
  const handleChangePage = (event, newPage) => setPagination(prev => ({ ...prev, currentPage: newPage }));
  const handleChangeRowsPerPage = (event) => setPagination(prev => ({ ...prev, pageSize: parseInt(event.target.value, 10), currentPage: 0 }));

  const openBulkSignDialog = () => {
    const unsignedReports = reports.filter(r => r.status?.toLowerCase() === "unsigned" || r.status?.toLowerCase() === "pending");
    if (unsignedReports.length === 0) {
      setSnackbarMessage(t("noUnsignedReportsToSign")); setSnackbarSeverity("info"); setSnackbarOpen(true); return;
    }
    setSignatureError(null); setUseProfileSignature(!!currentUser?.signature); setShowSignatureDialog(true);
  };

  const handleConfirmBulkSign = async () => {
    let signatureDataUrl = null; setSignatureError(null);
    if (!currentUser?.id || !currentUser?.name) {
      setBulkSignError(t("currentUserDataMissingError")); setSnackbarMessage(t("currentUserDataMissingError"));
      setSnackbarSeverity("error"); setSnackbarOpen(true); setShowSignatureDialog(false); return;
    }
    if (useProfileSignature && currentUser?.signature) signatureDataUrl = currentUser.signature;
    else if (sigPadRef.current && !sigPadRef.current.isEmpty()) signatureDataUrl = sigPadRef.current.getTrimmedCanvas().toDataURL("image/png");
    else { setSignatureError(t("signatureRequiredError")); return; }

    setShowSignatureDialog(false); setBulkSigning(true); setBulkSignError(null);
    const unsignedReportIds = reports.filter(r => r.status?.toLowerCase() === "unsigned" || r.status?.toLowerCase() === "pending").map(r => r._id);
    if (unsignedReportIds.length === 0) { /* ... same as openBulkSignDialog ... */ setBulkSigning(false); return; }

    try {
      const response = await reportService.bulkSign(unsignedReportIds, currentUser.id, currentUser.name, signatureDataUrl);
      setSnackbarMessage(t("bulkSignSuccess", { count: response.data?.signedCount || 0 })); setSnackbarSeverity("success");
      // removed fetchReportsAndExcavators
      if (sigPadRef.current && !useProfileSignature) sigPadRef.current.clear();
    } catch (err) {
      console.error("Error bulk signing:", err); const msg = err.response?.data?.message || t("bulkSignErrorGeneric");
      setBulkSignError(msg); setSnackbarMessage(msg); setSnackbarSeverity("error");
    } finally { setBulkSigning(false); setSnackbarOpen(true); }
  };

  const handleClearSignature = () => { if (sigPadRef.current) sigPadRef.current.clear(); setSignatureError(null); };
  const handleCloseSignatureDialog = () => { setShowSignatureDialog(false); setSignatureError(null); };
  const handleToggleSignatureSource = () => { setUseProfileSignature(prev => !prev); setSignatureError(null); if (useProfileSignature && sigPadRef.current) setTimeout(() => sigPadRef.current?.clear(), 0); };
  const handleCloseSnackbar = (event, reason) => { if (reason === "clickaway") return; setSnackbarOpen(false); };

  const formatStatus = (status) => {
    if (!status) return t("unknown");
    const statusKey = status.replace(/\s+/g, "").toLowerCase();
    return t(statusKey, status); // Pass status as fallback
  };

  const getStatusChip = (status) => {
    let color = "default"; let icon = <WarningIcon fontSize="inherit" />;
    switch (status?.toLowerCase()) {
      case "signed": case "completed": case "approved": color = "success"; icon = <CheckCircleIcon fontSize="inherit" />; break;
      case "unsigned": case "pending": color = "warning"; icon = <WarningIcon fontSize="inherit" />; break;
      case "scheduled": color = "info"; icon = <CalendarMonthIcon fontSize="inherit" />; break;
      default: break;
    }
    return <Chip icon={icon} label={formatStatus(status)} color={color} size="small" sx={{ fontWeight: 'medium', borderRadius: '6px' }} />;
  };

  const renderTableSkeleton = (rows = pagination.pageSize) => (
    <TableBody>{[...Array(rows)].map((_, i) => <TableRow key={`skel-${i}`}><TableCell colSpan={6}><Skeleton variant="text" height={40} /></TableCell></TableRow>)}</TableBody>
  );
  const renderCardSkeleton = (count = pagination.pageSize) => (
    <Grid container spacing={2.5}>{[...Array(count)].map((_, i) => <Grid item xs={12} sm={6} md={4} key={`skel-card-${i}`}><Skeleton variant="rectangular" height={220} sx={{ borderRadius: theme.shape.borderRadius }} /></Grid>)}</Grid>
  );
  const renderEmptyState = () => (
    <Paper sx={{ p: 4, textAlign: 'center', minHeight: 300, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
      <DescriptionIcon sx={{ fontSize: 50, color: 'text.disabled' }} />
      <Typography variant="h6" gutterBottom>{searchTerm ? t("noMatchingReports") : t("noReportsFound")}</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>{searchTerm ? t("tryDifferentSearch") : t("createFirstReportPrompt")}</Typography>
      <Button component={RouterLink} to="/reports/create" variant="contained" startIcon={<AddIcon />}>{t("createReport")}</Button>
    </Paper>
  );

  return (
    <PageContainer title={t("reports")}>
      <ContentSection
        title={t("reportsList")}
        icon={<DescriptionIcon color="primary" />}
        elevation={0} sx={{ p: 0 }}
        actions={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <FormControlLabel control={<Switch checked={viewMode === 'card'} onChange={() => setViewMode(prev => prev === 'table' ? 'card' : 'table')} />} label={viewMode === 'card' ? t('cardView') : t('tableView')} />
            </Box>
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}><IconButton onClick={() => setViewMode(prev => prev === 'table' ? 'card' : 'table')}>{viewMode === 'table' ? <GridViewIcon /> : <ViewListIcon />}</IconButton></Box>

            {isManager() && hasUnsignedReports && (
              <Button variant="contained" color="secondary" startIcon={bulkSigning ? <CircularProgress size={20} color="inherit" /> : <DoneAllIcon />} onClick={openBulkSignDialog} disabled={bulkSigning || !hasUnsignedReports}>
                {t("signAllUnsigned")}
              </Button>
            )}
            <Button component={RouterLink} to="/reports/create" variant="contained" startIcon={<AddIcon />}>{t("createReport")}</Button>
          </Stack>
        }>

        {error && <Alert severity="error" variant="outlined" sx={{ mb: 2 }}><AlertTitle>{t("error")}</AlertTitle>{error}</Alert>}
        {bulkSignError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBulkSignError(null)}>{bulkSignError}</Alert>}

        <SearchBar
            searchTerm={searchTerm}
            onSearchTermChange={e => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            t={t}
        />

        {loading && reports.length === 0 ? (viewMode === "table" ? <TableContainer component={Paper} sx={{ borderRadius: theme.shape.borderRadius }}><Table>{renderTableSkeleton()}</Table></TableContainer> : renderCardSkeleton()) : null}
        {!loading && reports.length === 0 ? renderEmptyState() : null}

        {!loading && reports.length > 0 && viewMode === "table" && (
          <TableContainer component={Paper} sx={{ borderRadius: theme.shape.borderRadius }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[{ id: 'status', label: t('status') }, { id: 'excavator.model', label: t('excavator') }, { id: 'operator.name', label: t('operator') }, { id: 'maintenanceDate', label: t('date') }].map(col => (
                    <TableCell key={col.id} sortDirection={sortBy === col.id ? sortDirection : false}>
                      <TableSortLabel active={sortBy === col.id} direction={sortBy === col.id ? sortDirection : 'asc'} onClick={() => handleSort(col.id)} IconComponent={UnfoldMoreIcon}>
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ width: 60 }}>{t("checks")}</TableCell>
                  <TableCell align="right">{t("actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((report) => (
                  <TableRow hover key={report._id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                    <TableCell>{getStatusChip(report.status)}</TableCell>
                    <TableCell>{report.excavator ? <><Typography variant="body2">{report.excavator.model}</Typography><Typography variant="caption" color="text.secondary">{report.excavator.manufacturer}</Typography></> : "-"}</TableCell>
                    <TableCell>{report.operator?.name || report.operator || "-"}</TableCell>
                    <TableCell><Typography variant="body2">{moment(report.maintenanceDate || report.createdAt).format("L")}</Typography><Typography variant="caption" color="text.secondary">{moment(report.createdAt).format("LT")}</Typography></TableCell>
                    <TableCell align="center">{renderCheckStatusIcon(report)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title={t("quickView")}><IconButton size="small" onClick={() => handleQuickView(report)}><VisibilityIcon /></IconButton></Tooltip>
                        <Tooltip title={t("viewDetails")}><IconButton size="small" component={RouterLink} to={`/reports/${report._id}`} color="primary"><DescriptionIcon /></IconButton></Tooltip>
                        <Tooltip title={t("downloadPdf")}><IconButton size="small" onClick={() => pdfService.downloadReportPDF(report._id)} color="secondary"><PictureAsPdfIcon /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={pagination.totalItems} rowsPerPage={pagination.pageSize} page={pagination.currentPage} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} labelRowsPerPage={t("rowsPerPage")} labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t("of")} ${count}`} />
          </TableContainer>
        )}

        {!loading && reports.length > 0 && viewMode === "card" && (
          <>
            <Grid container spacing={2.5}>
              {reports.map(report => (
                <Grid item xs={12} sm={6} md={4} key={report._id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', '&:hover': { boxShadow: theme.shadows[4] } }} elevation={1}>
                    <CardHeader
                      avatar={getStatusChip(report.status)}
                      action={renderCheckStatusIcon(report)}
                      title={<Typography variant="subtitle2" fontWeight="medium" noWrap>{report.type || t("maintenanceReport")}</Typography>}
                      subheader={<Typography variant="caption" color="text.secondary">#{report._id?.slice(-6)} • {moment(report.maintenanceDate || report.createdAt).format("L")}</Typography>}
                      sx={{ pb: 1, pt: 1.5, px: 2 }}
                    />
                    <CardContent sx={{ flexGrow: 1, pt: 0, px: 2 }}>
                      {report.excavator && <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}><BuildIcon fontSize="inherit" color="action" /><Typography variant="body2">{report.excavator.manufacturer} {report.excavator.model}</Typography></Stack>}
                      {report.operator && <Stack direction="row" alignItems="center" spacing={1}><PersonIcon fontSize="inherit" color="action" /><Typography variant="body2">{report.operator.name || report.operator}</Typography></Stack>}
                    </CardContent>
                    <Divider />
                    <CardActions sx={{ justifyContent: 'flex-end', p: 1 }}>
                      <Button size="small" onClick={() => handleQuickView(report)} startIcon={<VisibilityIcon />}>{t("quickView")}</Button>
                      <Button size="small" component={RouterLink} to={`/reports/${report._id}`} startIcon={<DescriptionIcon />} color="primary">{t("details")}</Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <TablePagination rowsPerPageOptions={[6, 9, 12, 24]} component="div" count={pagination.totalItems} rowsPerPage={pagination.pageSize} page={pagination.currentPage} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} labelRowsPerPage={t("cardsPerPage")} labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t("of")} ${count}`} />
          </>
        )}

        {/* Quick View Dialog & Signature Dialog & Snackbar remain largely the same, but ensure styling is inherited */}
        <Dialog open={showQuickViewModal} onClose={() => setShowQuickViewModal(false)} maxWidth="md" fullWidth scroll="paper">
          <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
            {t("reportQuickView")} - {currentReport?._id?.slice(-8)}
            <IconButton aria-label="close" onClick={() => setShowQuickViewModal(false)} sx={{ position: 'absolute', right: theme.spacing(1), top: theme.spacing(1), color: (theme) => theme.palette.grey[500] }}><ClearIcon /></IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            {currentReport && (
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium">{t("reportDetails")}</Typography>
                    <List dense disablePadding>
                      <ListItem><ListItemText primary={t("status")} secondary={getStatusChip(currentReport.status)} /></ListItem>
                      <ListItem><ListItemText primary={t("type")} secondary={currentReport.type || t("maintenanceReport")} /></ListItem>
                      <ListItem><ListItemText primary={t("maintenanceDate")} secondary={moment(currentReport.maintenanceDate || currentReport.createdAt).format("LL")} /></ListItem>
                      <ListItem><ListItemText primary={t("created")} secondary={moment(currentReport.createdAt).format("LL LT")} /></ListItem>
                    </List>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium">{t("equipmentAndPersonnel")}</Typography>
                    <List dense disablePadding>
                      {currentReport.excavator && <ListItem><ListItemIcon sx={{ minWidth: 32 }}><BuildIcon color="action" /></ListItemIcon><ListItemText primary={t("excavator")} secondary={`${currentReport.excavator.manufacturer} ${currentReport.excavator.model}`} /></ListItem>}
                      {currentReport.operator && <ListItem><ListItemIcon sx={{ minWidth: 32 }}><PersonIcon color="action" /></ListItemIcon><ListItemText primary={t("operator")} secondary={currentReport.operator.name || currentReport.operator} /></ListItem>}
                      {currentReport.technician && <ListItem><ListItemIcon sx={{ minWidth: 32 }}><PersonIcon color="action" /></ListItemIcon><ListItemText primary={t("technician")} secondary={currentReport.technician} /></ListItem>}
                    </List>
                  </Paper>
                </Grid>
                {currentReport.notes && <Grid item xs={12}><Paper variant="outlined" sx={{ p: 2 }}><Typography variant="subtitle1" gutterBottom fontWeight="medium">{t("notes")}</Typography><Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{currentReport.notes}</Typography></Paper></Grid>}
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Button onClick={() => setShowQuickViewModal(false)} color="secondary">{t("close")}</Button>
            <Button component={RouterLink} to={`/reports/${currentReport?._id}`} variant="contained" startIcon={<DescriptionIcon />} onClick={() => setShowQuickViewModal(false)}>{t("viewFullDetails")}</Button>
            <Button variant="outlined" color="primary" startIcon={<PictureAsPdfIcon />} onClick={() => { pdfService.downloadReportPDF(currentReport?._id); setShowQuickViewModal(false); }}>{t("downloadPdf")}</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={showSignatureDialog} onClose={handleCloseSignatureDialog} maxWidth="xs" fullWidth>
          <DialogTitle>{t("provideSignatureForBulkSign")}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 1.5 }}>{currentUser?.signature ? (useProfileSignature ? t("confirmProfileSignature") : t("drawNewSignaturePrompt")) : t("drawSignaturePrompt")}</DialogContentText>
            {currentUser?.signature && <FormControlLabel control={<Switch checked={!useProfileSignature} onChange={handleToggleSignatureSource} icon={<AccountCircleIcon />} checkedIcon={<EditIcon />} />} label={useProfileSignature ? t("switchToDrawSignature") : t("switchToUseProfileSignature")} sx={{ mb: 1.5, display: 'block' }} />}
            {useProfileSignature && currentUser?.signature ? (
              <Paper variant="outlined" sx={{ p: 1, textAlign: 'center', borderColor: signatureError ? "error.main" : "divider" }}><img src={currentUser.signature} alt={t("profileSignatureAlt")} style={{ maxWidth: "100%", height: "150px", border: `1px solid ${theme.palette.divider}` }} /></Paper>
            ) : (
              <Paper variant="outlined" sx={{ borderColor: signatureError ? "error.main" : "divider", position: "relative", height: 180 }}>
                <SignatureCanvas ref={sigPadRef} penColor={theme.palette.text.primary} canvasProps={{ style: { width: "100%", height: '100%' }, className: "sigCanvas" }} onBegin={() => setSignatureError(null)} />
                <IconButton onClick={handleClearSignature} size="small" sx={{ position: "absolute", top: 4, right: 4, color: "text.secondary" }} title={t("clearSignature")}><ClearIcon /></IconButton>
              </Paper>
            )}
            {signatureError && <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>{signatureError}</Typography>}
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
            <Button onClick={handleCloseSignatureDialog} color="secondary">{t("cancel")}</Button>
            <Button onClick={handleConfirmBulkSign} variant="contained" color="primary" disabled={bulkSigning} startIcon={bulkSigning ? <CircularProgress size={20} color="inherit" /> : null}>{t("confirmAndSign")}</Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} variant="filled" sx={{ width: "100%" }}>{snackbarMessage}</Alert>
        </Snackbar>
      </ContentSection>
    </PageContainer>
  );
};

export default ReportList;
